import React, { useState, useRef, useLayoutEffect, Fragment } from 'react';
import { AnimatePresence } from 'framer-motion';
import * as S from './action-menu.styles';
import { ActionMenuItemProps, ActionMenuProps } from './action-menu.types';
import { useButton, useFocusRing, useInteractOutside } from 'react-aria';
import { Typography } from '@design-system/atoms/typography';
import { Icon } from '@design-system/atoms/icon';
import Portal from '@design-system/atoms/portal/Portal';

const VIEWPORT_PADDING = 8;

const ActionMenuRoot: React.FC<ActionMenuProps> = ({ 'data-testid': dataTest, usePortal = true, ...props }) => {
  const { title, triggerRef, isOpen, onClose, placement = 'right', className, children } = props;
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current || !menuRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const menuEl = menuRef.current;
    const menuHeight = menuEl.offsetHeight;
    const menuWidth = menuEl.offsetWidth;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'top':
        top = rect.top - menuHeight - VIEWPORT_PADDING;
        left = rect.left + rect.width / 2;
        break;
      case 'bottom':
        top = rect.bottom + VIEWPORT_PADDING;
        left = rect.left + rect.width / 2;
        break;
      case 'left':
        top = rect.top;
        left = rect.left - menuWidth - VIEWPORT_PADDING;
        break;
      case 'right':
      default:
        top = rect.top;
        left = rect.right + VIEWPORT_PADDING;
        break;
    }

    // Keep menu inside viewport
    if (placement === 'left' || placement === 'right') {
      if (top + menuHeight > viewportHeight - VIEWPORT_PADDING) {
        top = viewportHeight - menuHeight - VIEWPORT_PADDING;
      }
      if (top < VIEWPORT_PADDING) {
        top = VIEWPORT_PADDING;
      }
      if (placement === 'left' && left < VIEWPORT_PADDING) {
        left = VIEWPORT_PADDING;
      }
      if (placement === 'right' && left + menuWidth > viewportWidth - VIEWPORT_PADDING) {
        left = viewportWidth - menuWidth - VIEWPORT_PADDING;
      }
    } else if (placement === 'bottom' || placement === 'top') {
      if (left - menuWidth / 2 < VIEWPORT_PADDING) {
        left = VIEWPORT_PADDING + menuWidth / 2;
      }
      if (left + menuWidth / 2 > viewportWidth - VIEWPORT_PADDING) {
        left = viewportWidth - menuWidth / 2 - VIEWPORT_PADDING;
      }
    }

    setPosition({ top, left });
  }, [isOpen, triggerRef, placement]);

  useInteractOutside({
    ref: menuRef,
    onInteractOutside: onClose,
  });

  // Define animation properties based on placement
  const getAnimationProps = () => {
    switch (placement) {
      case 'top':
        return {
          initial: { opacity: 0, y: 10, x: '-50%' },
          animate: { opacity: 1, y: 0, x: '-50%' },
          exit: { opacity: 0, y: 10, x: '-50%' },
        };
      case 'bottom':
        return {
          initial: { opacity: 0, y: -10, x: '-50%' },
          animate: { opacity: 1, y: 0, x: '-50%' },
          exit: { opacity: 0, y: -10, x: '-50%' },
        };
      case 'left':
        return {
          initial: { opacity: 0, x: 10 },
          animate: { opacity: 1, x: 0 },
          exit: { opacity: 0, x: 10 },
        };
      case 'right':
      default:
        return {
          initial: { opacity: 0, x: -10 },
          animate: { opacity: 1, x: 0 },
          exit: { opacity: 0, x: -10 },
        };
    }
  };

  const WrapperComponent = usePortal ? Portal : Fragment;

  return (
    <WrapperComponent>
      <AnimatePresence>
        {isOpen && (
          <S.ActionMenuContainer
            ref={menuRef}
            className={className}
            placement={placement}
            transition={{ duration: 0.2 }}
            style={{
              top: position.top,
              left: position.left,
            }}
            hasTitle={!!title}
            data-testid={dataTest || 'action-menu'}
            {...getAnimationProps()}
          >
            {title && (
              <S.ActionMenuHeader variant="body-16" as="h4" fontWeight={600} data-testid="action-menu-title">
                {title}
              </S.ActionMenuHeader>
            )}
            {children}
          </S.ActionMenuContainer>
        )}
      </AnimatePresence>
    </WrapperComponent>
  );
};

const Item: React.FC<ActionMenuItemProps> = (props) => {
  const { label, onPress, isActive, id, startIcon: StartIconProp, isDisabled, isDanger, ...restItem } = props;
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Use React Aria's useButton hook for accessibility
  const { buttonProps } = useButton(
    {
      onPress,
      'aria-label': label,
    },
    buttonRef
  );

  // Use React Aria's useFocusRing hook for focus state
  const { isFocusVisible, focusProps } = useFocusRing();

  return (
    <S.Item
      isFocusVisible={isFocusVisible}
      isActive={isActive}
      isDisabled={isDisabled}
      isDanger={isDanger}
      {...restItem}
      {...buttonProps}
      {...focusProps}
      ref={buttonRef}
    >
      {StartIconProp &&
        (typeof StartIconProp === 'string' ? <Icon name={StartIconProp} size={16} /> : <StartIconProp size={16} />)}
      <Typography variant="body-12" as="span" fontWeight={isActive ? 600 : undefined} style={{ lineHeight: '16px' }}>
        {label}
      </Typography>
    </S.Item>
  );
};

export const ActionMenu = Object.assign(ActionMenuRoot, {
  Item,
});
